import React from 'react';
import { Question, UserAnswer } from '../types';

interface QuestionNavigationProps {
    questions: Question[];
    userAnswers: Map<number, UserAnswer>;
    markedForReview: Set<number>;
    currentQuestionIndex: number;
    onJumpToQuestion: (index: number) => void;
    reviewMode?: boolean;
    displayLanguage?: 'pt' | 'en' | 'es';
}

const QuestionNavigation: React.FC<QuestionNavigationProps> = ({
    questions,
    userAnswers,
    markedForReview,
    currentQuestionIndex,
    onJumpToQuestion,
    reviewMode = false,
    displayLanguage = 'pt'
}) => {
    const getButtonClass = (index: number) => {
        const questionId = questions[index]?.id;
        if (!questionId) return 'bg-gray-200 dark:bg-slate-700 border border-gray-300 dark:border-slate-600';

        const userAnswer = userAnswers.get(questionId);
        const isAnswered = !!userAnswer;
        const isCurrent = index === currentQuestionIndex;
        const isMarked = markedForReview.has(questionId);

        let baseClass = 'w-9 h-9 flex items-center justify-center rounded-md font-bold transition-all duration-200 transform hover:scale-110';
        let colorClass = '';

        if (reviewMode) {
            // Review Screen Logic
            if (isAnswered) {
                colorClass = userAnswer.isCorrect 
                    ? 'bg-green-600 hover:bg-green-500 text-white' // Correct
                    : 'bg-red-600 hover:bg-red-500 text-white';    // Incorrect
            } else {
                colorClass = 'bg-red-600 hover:bg-red-500 text-white';    // Unanswered
            }
        } else {
            // Quiz Screen Logic
            if (isMarked) {
                colorClass = 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-md shadow-yellow-500/30'; // Marked
            } else if (isAnswered) {
                colorClass = 'bg-green-600 hover:bg-green-500 text-white'; // Answered
            } else {
                colorClass = 'bg-gray-200 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300'; // Not Answered
            }
        }

        if (isCurrent) {
            baseClass += ' ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-800 ring-orange-400 scale-110';
        }

        return `${baseClass} ${colorClass}`;
    };

    return (
        <div className="bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-lg shadow-2xl mb-6 animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 text-center">{displayLanguage === 'en' ? 'Question Navigation' : displayLanguage === 'es' ? 'Navegación de preguntas' : 'Navegação de Questões'}</h3>
            <div className="flex flex-wrap justify-center gap-2">
                {questions.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => onJumpToQuestion(index)}
                        className={getButtonClass(index)}
                        aria-label={displayLanguage === 'en' ? `Go to question ${index + 1}` : displayLanguage === 'es' ? `Ir a la pregunta ${index + 1}` : `Ir para a questão ${index + 1}`}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
            <div className="flex justify-center flex-wrap gap-x-4 gap-y-2 mt-4 text-sm text-gray-500 dark:text-slate-400">
                {reviewMode ? (
                    <>
                        <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-green-600 mr-2"></span> {displayLanguage === 'en' ? 'Correct' : displayLanguage === 'es' ? 'Correcta' : 'Correta'}</div>
                        <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-red-600 mr-2"></span> {displayLanguage === 'en' ? 'Incorrect / Unanswered' : displayLanguage === 'es' ? 'Incorrecta / No respondida' : 'Incorreta / Não Respondida'}</div>
                    </>
                ) : (
                    <>
                        <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-green-600 mr-2"></span> {displayLanguage === 'en' ? 'Answered' : displayLanguage === 'es' ? 'Respondida' : 'Respondida'}</div>
                        <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></span> {displayLanguage === 'en' ? 'Marked' : displayLanguage === 'es' ? 'Marcada' : 'Marcada'}</div>
                        <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-gray-200 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 mr-2"></span> {displayLanguage === 'en' ? 'Unanswered' : displayLanguage === 'es' ? 'No respondida' : 'Não Respondida'}</div>
                    </>
                )}
                 <div className="flex items-center"><span className="w-3 h-3 rounded-full ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-800 ring-orange-400 mr-2"></span> {displayLanguage === 'en' ? 'Current' : displayLanguage === 'es' ? 'Actual' : 'Atual'}</div>
            </div>
        </div>
    );
};

export default QuestionNavigation;